import {
    Button,
    Checkbox,
    Collapse,
    Grid,
    IconButton,
    makeStyles,
    MenuItem,
    Select,
    TableCell,
    TableRow,
    TextField
} from '@material-ui/core';
import React, { useState } from 'react';
import PriceItemRule, { useAutocompleteStyles } from './admin/PriceItemRule';
import { usePriceItemruleConditions } from '../hooks/usePriceItemruleConditions';
import { ChevronDownIcon, ChevronUpIcon, TimesIcon } from '../icons';
import AutocompleteSelect from './atom/AutocompleteSelect';
import { priceItemAutocomplete } from '../autocomplete';

const useRowStyles = makeStyles({
    root: {
        '& > *': {
            borderBottom: 'unset'
        }
    }
});

const generateHashChars = len => Math.floor(Math.random() * 16 ** len).toString(16);

const PriceItem = ({ item, setItem, removePriceItem, bookingFields, chainPriceItemFlag }) => {
    const { name, is_taxable, is_fee, account_to, net_usd, net_percentage, type, rules } = item;
    const [open, setOpen] = useState(false);
    const classes = useRowStyles();
    const autocompleteClasses = useAutocompleteStyles();

    const [conditionTypes] = usePriceItemruleConditions(bookingFields || []);

    return (
        <>
            <TableRow className={classes.root}>
                <TableCell>
                    <IconButton onClick={() => removePriceItem(item)}>
                        <TimesIcon color="disabled" />
                    </IconButton>
                </TableCell>
                <TableCell style={{ minWidth: 200 }}>
                    <AutocompleteSelect
                        allowCustom
                        initCall={priceItemAutocomplete.init}
                        autocompleteCall={priceItemAutocomplete.search}
                        value={name}
                        setValue={(val, getItem) => {
                            if (typeof val === 'string') {
                                setItem({ ...item, price_item_name_id: null, name: val });
                                return;
                            }
                            const selected = getItem(val);
                            setItem({
                                ...item,
                                price_item_name_id: val,
                                name: selected?.name ?? ''
                            });
                        }}
                        disableClearable={!name}
                        classes={{
                            clearIndicator: autocompleteClasses.clearIndicator
                        }}
                    >
                        <TextField />
                    </AutocompleteSelect>
                </TableCell>
                <TableCell>
                    <Checkbox
                        checked={is_taxable}
                        onChange={() => setItem({ ...item, is_taxable: !item.is_taxable })}
                    />
                </TableCell>
                <TableCell>
                    <Checkbox
                        checked={is_fee}
                        onChange={() => setItem({ ...item, is_fee: !item.is_fee })}
                    />
                </TableCell>
                <TableCell>
                    <Select
                        value={account_to}
                        onChange={event => setItem({ ...item, account_to: event.target.value })}
                    >
                        <MenuItem value="vendor">Vendor</MenuItem>
                        <MenuItem value="agent">Agent</MenuItem>
                        <MenuItem value="client">Client</MenuItem>
                        <MenuItem value="system">System</MenuItem>
                        {chainPriceItemFlag && <MenuItem value="chain">Chain</MenuItem>}
                    </Select>
                </TableCell>
                <TableCell>
                    <TextField
                        placeholder="Net USD"
                        type="number"
                        inputProps={{ step: 0.01 }}
                        value={net_usd}
                        onChange={event =>
                            setItem({ ...item, net_usd: parseFloat(event.target.value) })
                        }
                    />
                </TableCell>
                <TableCell>
                    <TextField
                        placeholder="Net %"
                        type="number"
                        inputProps={{ step: 0.01 }}
                        value={net_percentage}
                        onChange={event =>
                            setItem({ ...item, net_percentage: parseFloat(event.target.value) })
                        }
                    />
                </TableCell>
                <TableCell>
                    <Select
                        value={type}
                        onChange={event => setItem({ ...item, type: event.target.value })}
                    >
                        <MenuItem value="lowest">Lowest</MenuItem>
                        <MenuItem value="highest">Highest</MenuItem>
                    </Select>
                </TableCell>
                <TableCell>
                    <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                        {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    </IconButton>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell
                    style={{
                        paddingBottom: 0,
                        paddingTop: 0,
                        backgroundColor: rules.length ? '#fafafa' : 'transparent'
                    }}
                    colSpan={10}
                >
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Grid
                            container
                            justifyContent="flex-end"
                            style={{ marginTop: 8, padding: '8px' }}
                        >
                            {rules.map((rule, ruleIdx) => (
                                <PriceItemRule
                                    rule={rule}
                                    setRule={editedRule =>
                                        setItem({
                                            ...item,
                                            rules: item.rules.map(r =>
                                                r.id === editedRule.id ? editedRule : r
                                            )
                                        })
                                    }
                                    removeRule={() =>
                                        setItem({
                                            ...item,
                                            rules: item.rules.filter(r => r.id !== rule.id)
                                        })
                                    }
                                    duplicateRule={() =>
                                        setItem({
                                            ...item,
                                            rules: [
                                                ...item.rules.slice(0, ruleIdx + 1),
                                                { ...rule, id: generateHashChars(12) },
                                                ...item.rules.slice(ruleIdx + 1)
                                            ]
                                        })
                                    }
                                    key={rule.id}
                                    conditionTypes={conditionTypes}
                                />
                            ))}
                            <Grid item xs={12}>
                                <Button
                                    variant="text"
                                    size="small"
                                    fullWidth
                                    onClick={() =>
                                        setItem({
                                            ...item,
                                            rules: item.rules.concat([
                                                {
                                                    id: generateHashChars(12),
                                                    conditions: [
                                                        {
                                                            id: generateHashChars(8),
                                                            type: 'usergroup',
                                                            value: []
                                                        }
                                                    ],
                                                    net_usd: 0,
                                                    net_percentage: 0
                                                }
                                            ])
                                        })
                                    }
                                >
                                    Add new price item rule
                                </Button>
                            </Grid>
                        </Grid>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

export default PriceItem;
